<template>
  <v-container class="p-4">
    <section class="d-flex flex-column flex-sm-row justify-content-between">
      <h2 class="ml-1 my-5">Matrices de aprobación</h2>
      <v-breadcrumbs :items="breadcrumb"></v-breadcrumbs>
    </section>
    <p v-if="!filters.business_unit" class="text-info mt-5">
      <v-icon color="blue lighten-2" class="mr-1">
        mdi-information-outline
      </v-icon>
      <span class="mt-3">
        <b>ATENCIÓN:</b> Para iniciar la búsqueda debe seleccionar una unidad de
        negocio
      </span>
    </p>
    <v-row align-content="center" align-md="center" class="mt-0">
      <v-col cols="12" md="4" sm="12" class="py-0">
        <v-autocomplete
          v-model="filters.business_unit"
          :items="businessUnits"
          @input="getApprovalsMatrixData(1)"
          class=""
          item-text="name"
          item-value="sap_code"
          no-data-text="Sin Unidades de Negocio"
          label="Filtrar por Unidad de Negocio para iniciar la búsqueda"
        ></v-autocomplete>
      </v-col>
      <v-col cols="12" md="4" sm="12" class="py-0">
        <v-text-field
          label="Busqueda"
          :disabled="!filters.business_unit"
          v-model="filters.filter"
          clearable
          @input="getApprovalsMatrixData(1)"
          placeholder="Buscar matriz"
          append-icon="mdi-magnify"
        ></v-text-field>
      </v-col>
    </v-row>
    <p class="text-right mt-5">
      Número de matrices total:
      {{ approvalsMatrix.count }}
    </p>
    <v-data-table
      class="elevation-1 px-5 py-3 mt-1"
      :headers="tableHeaders"
      :items="approvalsMatrix.approval_matrix"
      :items-per-page="25"
      :sort-by="sortBy"
      :footer-props="{ 'items-per-page-options': [10, 25, 50, 100] }"
      :sort-desc="sortDesc"
      :loading="loading"
      loading-text="Cargando matrices"
      item-key="id"
      no-data-text="Sin matrices"
      @page-count="pageCount = $event"
    >
      <template v-slot:item.actions="{ item }">
        <div class="d-flex align-items-center justify-content-center">
          <v-tooltip top>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                @click="openModalUpdateOrCreateApprovalMatrix(item)"
                icon
                color="primary"
                v-bind="attrs"
                v-on="on"
              >
                <v-icon> mdi-pencil </v-icon>
              </v-btn>
            </template>
            <span>Editar</span>
          </v-tooltip>
        </div>
      </template>
      <template v-slot:item.management="{ item }">
        <span>
          {{
            item.management
              ? `${item.employee_category.name} (${item.management.name})`
              : "-"
          }}
        </span>
      </template>
      <template v-slot:item.max_amount="{ item }">
        <span>
          {{
            item.max_amount
              ? `$ ${item.max_amount.toLocaleString("es", optionsFormatting)}`
              : "-"
          }}
        </span>
      </template>
    </v-data-table>
    <div class="d-flex justify-content-end pt-2 pb-1">
      <v-pagination
        v-model="filters.page"
        :disabled="loading"
        class="v-application"
        :length="approvalsMatrix.pages ? approvalsMatrix.pages : 1"
        :total-visible="8"
      ></v-pagination>
    </div>
    <update-approval-matrix
      :modalAction="dialogUpdateOrCreateApprovalMatrix"
      :closeModalApprovalMatrix="closeModalUpdateOrCreateApprovalMatrix"
      :currentApprovalMatrix="currentApprovalMatrix"
      @approveMatrixUpdated="getApprovalsMatrixData"
    ></update-approval-matrix>
  </v-container>
</template>

<script>
import { preventCloseIfNotKeyEventEsc } from "@/helpers/common";
import { mapActions, mapGetters } from "vuex";
import UpdateApprovalMatrix from "./UpdateApprovalMatrix.vue";
import config from "@/config";

export default {
  components: { UpdateApprovalMatrix },
  mounted() {
    this.getBusinessUnits();
  },
  data() {
    return {
      optionsFormatting: { style: "decimal", useGrouping: true },
      breadcrumb: [
        {
          text: "Proyectos",
          disabled: false,
          href: "/",
        },
        {
          text: "Matriz de aprobación",
          disabled: true,
          href: "/mantenedores/matrizAprobacion",
        },
      ],
      dialogUpdateOrCreateApprovalMatrix: false,
      currentApprovalMatrix: null,
      loading: false,
      debounce: null,
      debounceTime: 500,
      filters: {
        filter: "",
        page: 1,
        business_unit: null,
      },
      pageCount: 0,
      sortBy: ["business_unit.name", "max_amount"],
      sortDesc: [true, true],
      tableHeaders: [
        {
          text: "Acciones",
          align: "center",
          sortable: false,
          value: "actions",
        },
        {
          text: "Unidad de Negocio",
          align: "center",
          sortable: true,
          value: "business_unit.name",
        },
        {
          text: "Categoria",
          align: "center",
          sortable: false,
          value: "employee_category.name",
        },
        {
          text: "Monto Máximo",
          align: "center",
          sortable: true,
          value: "max_amount",
        },
      ],
    };
  },
  methods: {
    ...mapActions({
      getBusinessUnits: "requestMaterial/getBusinessUnits",
      getApprovalsMatrix: "maintainers/getApprovalsMatrix",
    }),
    async getApprovalsMatrixData(page = 1) {
      clearTimeout(this.debounce);
      let self = this;
      this.debounce = setTimeout(async function () {
        self.loading = true;
        await self.getApprovalsMatrix({
          password: config.password_manteiners,
          page: page - 1,
          filter: self.filters.filter ? self.filters.filter : undefined,
          business_unit_code: self.filters.business_unit
            ? self.filters.business_unit
            : undefined,
        });
        self.loading = false;
      }, self.debounceTime);
    },
    openModalUpdateOrCreateApprovalMatrix(approvalMatrix) {
      this.currentApprovalMatrix = approvalMatrix;
      this.dialogUpdateOrCreateApprovalMatrix = true;
    },
    closeModalUpdateOrCreateApprovalMatrix($event) {
      let validationEvent = preventCloseIfNotKeyEventEsc($event);
      if (validationEvent) {
        this.currentApprovalMatrix = null;
        this.dialogUpdateOrCreateApprovalMatrix = false;
      }
    },
  },
  computed: {
    ...mapGetters({
      approvalsMatrix: "maintainers/approvalsMatrix",
      businessUnits: "requestMaterial/businessUnits",
    }),
  },
  watch: {
    "filters.page": function (page) {
      this.getApprovalsMatrixData(page);
    },
  },
};
</script>

<style></style>